/**
 * ProfileMenu Component
 * User profile dropdown menu with avatar and options
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Tooltip,
  useTheme,
  Box,
} from '@mui/material';
import {
  Person as PersonIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import ThemeToggle from '@/components/layout/ThemeToggle';
import useAuthStore from '@/store/authStore';
import useSystemStatus from '@/hooks/useSystemStatus';
import { useWebSocket } from '@/hooks/useWebSocket';
import SystemStatusModal from '@/components/layout/SystemStatusModal';

export default function ProfileMenu() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selected, setSelected] = useState<string | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    // Ensure the system status modal is closed when opening the profile menu
    setStatusModalOpen(false);
    setSelected(null);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleProfile = () => {
    handleClose();
    navigate('/profile');
  };

  const handleSettings = () => {
    handleClose();
    navigate('/config');
  };

  const handleLogout = async () => {
    handleClose();
    await logout();
    navigate('/auth');
  };

  const getInitials = () => {
    if (!user?.username) return null;
    return user.username.substring(0, 2).toUpperCase();
  };

  // Support optional photo fields if backend adds them in the future
  const photoUrl = (user as any)?.photoUrl || (user as any)?.avatarUrl || null;
  const initials = getInitials();
  const { status, details, errors, health, refresh } = useSystemStatus();
  const { connectionState, connectionReason, lastMessageAt } = useWebSocket();
  const [statusModalOpen, setStatusModalOpen] = useState(false);

  const getStatusColor = () => {
    switch (status) {
      case 'ok':
        return 'success.main';
      case 'warning':
        return 'warning.main';
      case 'error':
        return 'error.main';
      default:
        return 'grey.500';
    }
  };

  const getStatusLabel = () => {
    switch (status) {
      case 'ok':
        return 'Operativo';
      case 'warning':
        return 'Advertencia';
      case 'error':
        return 'Error';
      default:
        return 'Desconocido';
    }
  };

  return (
    <>
      <Tooltip title="Perfil">
        <IconButton
          onClick={handleClick}
          size="small"
          color="inherit"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
          }}
          aria-label="perfil de usuario"
        >
          <Avatar
            src={photoUrl ?? undefined}
            sx={{
              width: 32,
              height: 32,
              bgcolor: 'transparent',
              color: 'inherit',
              fontSize: '0.875rem',
            }}
          >
            {initials ?? <PersonIcon fontSize="small" color="inherit" />}
          </Avatar>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            // small top margin so the menu doesn't get hidden under fixed app bars
            mt: 1,
            // spacing and layout to match FloatingControlBar
            p: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            // on small screens add extra margin to account for safe-area / notch
            '@media (max-width:600px)': {
              mt: 'calc(8px + env(safe-area-inset-top, 0px))',
            },
          },
        }}
      >
        <MenuItem
          sx={{
            alignItems: 'center',
            height: 48,
            px: 2,
            py: 1,
            gap: 1,
            borderRadius: 1,
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <Avatar
              src={photoUrl ?? undefined}
              sx={{ width: 32, height: 32, bgcolor: 'transparent' }}
            >
              {initials ?? <PersonIcon fontSize="small" color="inherit" />}
            </Avatar>
          </ListItemIcon>

          <ListItemText
            primary={user?.username ?? 'Usuario'}
            secondary={user?.role === 'admin' ? 'Administrador' : (user?.username ?? 'Usuario')}
            sx={{ mr: 1 }}
          />

          <ThemeToggle />
        </MenuItem>

        <MenuItem
          onClick={() => {
            setSelected('profile');
            handleProfile();
          }}
          sx={{
            height: 48,
            px: 2,
            borderRadius: 1,
            gap: 1.5,
            display: 'flex',
            alignItems: 'center',
            transition: theme.transitions.create(['background-color', 'box-shadow', 'color'], {
              duration: theme.transitions.duration.shorter,
            }),
            '&:hover':
              selected === 'profile'
                ? {
                    backgroundColor: theme.palette.primary.dark,
                    color: theme.palette.common.white,
                    boxShadow: theme.shadows[4],
                  }
                : {
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.common.white,
                    boxShadow: theme.shadows[4],
                  },
            ...(selected === 'profile' && {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.common.white,
              boxShadow: theme.shadows[4],
            }),
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Mi Perfil</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => {
            setSelected('settings');
            handleSettings();
          }}
          sx={{
            height: 48,
            px: 2,
            borderRadius: 1,
            gap: 1.5,
            display: 'flex',
            alignItems: 'center',
            transition: theme.transitions.create(['background-color', 'box-shadow', 'color'], {
              duration: theme.transitions.duration.shorter,
            }),
            '&:hover':
              selected === 'settings'
                ? {
                    backgroundColor: theme.palette.primary.dark,
                    color: theme.palette.common.white,
                    boxShadow: theme.shadows[4],
                  }
                : {
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.common.white,
                    boxShadow: theme.shadows[4],
                  },
            ...(selected === 'settings' && {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.common.white,
              boxShadow: theme.shadows[4],
            }),
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Configuración</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => {
            setSelected('logout');
            handleLogout();
          }}
          sx={{
            height: 48,
            px: 2,
            borderRadius: 1,
            gap: 1.5,
            display: 'flex',
            alignItems: 'center',
            transition: theme.transitions.create(['background-color', 'box-shadow', 'color'], {
              duration: theme.transitions.duration.shorter,
            }),
            '&:hover':
              selected === 'logout'
                ? {
                    backgroundColor: theme.palette.primary.dark,
                    color: theme.palette.common.white,
                    boxShadow: theme.shadows[4],
                  }
                : {
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.common.white,
                    boxShadow: theme.shadows[4],
                  },
            ...(selected === 'logout' && {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.common.white,
              boxShadow: theme.shadows[4],
            }),
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Cerrar Sesión</ListItemText>
        </MenuItem>

        {/* Minimal system status indicator at menu bottom */}
        <MenuItem
          onClick={() => {
            handleClose();
            setStatusModalOpen(true);
          }}
          aria-label={`Estado del sistema: ${getStatusLabel()}`}
          sx={{
            mt: 0.5,
            alignItems: 'center',
            height: 48,
            px: 1,
            borderRadius: 1,
            display: 'flex',
            justifyContent: 'center',
            backgroundColor: 'transparent',
          }}
        >
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}
          >
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: getStatusColor(),
                border: '1px solid',
                borderColor: 'divider',
              }}
              role="img"
              aria-hidden
              title={getStatusLabel()}
            />
          </Box>
        </MenuItem>
      </Menu>

      <SystemStatusModal
        open={statusModalOpen}
        onClose={() => setStatusModalOpen(false)}
        status={status}
        details={details}
        errors={errors}
        health={health}
        wsState={connectionState}
        wsReason={connectionReason}
        wsLastMessageAt={lastMessageAt}
        onRefresh={refresh}
      />
    </>
  );
}
